import { useEffect, useState } from 'react';

import type { Ergebnis, Settings } from '@baba/engine/types';
import { api, setzeWort } from '../api.ts';
import { datum, euro, zahl, zuText, zuZahl, NAME } from '../format.ts';
import { Zaehlwerk, Schild } from './Zaehlwerk.tsx';

interface Props {
  ergebnis: Ergebnis;
  settings: Settings;
  aufSettings: (s: Settings) => Promise<void>;
}

export function Startseite({ ergebnis, settings, aufSettings }: Props) {
  const { ausgleich, instandhaltung, parameter } = ergebnis;
  const [zusatz, setZusatz] = useState(
    zuText(settings.zusatzkostenProKmDefault, 2),
  );
  const [anteil, setAnteil] = useState(
    zuText(100 * settings.anteilEinzelstreckeDefault, 0),
  );
  /*
    Bewusst ohne Tausenderpunkt: zuZahl liest "184.824" als 184, weil ohne
    Komma der Punkt als Dezimaltrenner gilt. Ein hübsch formatiertes Feld
    zerstörte hier bei jeder Eingabe den Zählerstand.
  */
  const [initKm, setInitKm] = useState(zuText(settings.initKmStand, 0));

  useEffect(() => {
    setZusatz(zuText(settings.zusatzkostenProKmDefault, 2));
  }, [settings.zusatzkostenProKmDefault]);

  useEffect(() => {
    setAnteil(zuText(100 * settings.anteilEinzelstreckeDefault, 0));
  }, [settings.anteilEinzelstreckeDefault]);

  useEffect(() => {
    setInitKm(zuText(settings.initKmStand, 0));
  }, [settings.initKmStand]);

  async function uebernehmen() {
    const wert = zuZahl(zusatz);
    if (wert == null || wert < 0) {
      setZusatz(zuText(settings.zusatzkostenProKmDefault, 2));
      return;
    }
    if (wert === settings.zusatzkostenProKmDefault) return;
    await aufSettings({ ...settings, zusatzkostenProKmDefault: wert });
  }

  /**
   * Der Anteil wird als Prozentzahl eingegeben - "65" liest sich leichter als
   * "0,65" und lässt weniger Raum für Kommafehler.
   */
  async function anteilUebernehmen() {
    const prozent = zuZahl(anteil);
    if (prozent == null || prozent <= 0 || prozent > 100) {
      setAnteil(zuText(100 * settings.anteilEinzelstreckeDefault, 0));
      return;
    }
    const wert = prozent / 100;
    if (wert === settings.anteilEinzelstreckeDefault) return;
    await aufSettings({ ...settings, anteilEinzelstreckeDefault: wert });
  }

  /**
   * Der importierte Zählerstand (R-16).
   *
   * Er ist der Anfang der Zählerstandskette und bestimmt damit die
   * Jahreskilometer der Statistik (R-10) - deshalb der Hinweis am Feld.
   */
  async function initKmUebernehmen() {
    const wert = zuZahl(initKm);
    if (wert == null || wert < 0) {
      setInitKm(zuText(settings.initKmStand, 0));
      return;
    }
    if (wert === settings.initKmStand) return;
    await aufSettings({ ...settings, initKmStand: wert });
  }

  return (
    <>
      <section className="zaehlwerk">
        {ausgleich.schuldner && ausgleich.glaeubiger ? (
          <>
            <Schild
              schuldner={NAME[ausgleich.schuldner]}
              glaeubiger={NAME[ausgleich.glaeubiger]}
            />
            <div>
              <Zaehlwerk betrag={ausgleich.betrag} />
            </div>
          </>
        ) : (
          <p className="zaehlwerk__ruhe">
            Alles ausgeglichen. Michi und Otto sind quitt.
          </p>
        )}
      </section>

      <section className="tafel">
        <p className="etikett">Konto Instandhaltung</p>
        <div className="zeile">
          <span>Kassierte Beiträge</span>
          <span className="wert">{euro(instandhaltung.einzahlungen)}</span>
        </div>
        <div className="zeile">
          <span>Bezahlte Reparaturen</span>
          <span className="wert">−{euro(instandhaltung.auszahlungen)}</span>
        </div>
        <div className="zeile zeile--summe">
          <span>Kontostand</span>
          <span
            className={instandhaltung.ausreichend ? 'wert' : 'wert wert--minus'}
          >
            {euro(instandhaltung.kontostand)}
          </span>
        </div>
      </section>

      <section className="tafel">
        <p className="etikett">Aktuelle Rechenwerte</p>
        {parameter.tankvorgaenge.length === 0 ? (
          <p className="leer">
            Noch keine Volltankung erfasst. Bis dahin gelten{' '}
            {zahl(settings.startVerbrauchL100km, 1)} l/100 km und{' '}
            {zahl(settings.startPreisProLiter, 2)} € je Liter.
          </p>
        ) : (
          <>
            <div className="rechenwerte">
              <div className="rechenwerte__kopf">
                <span>Datum</span>
                <span>€/l</span>
                <span>l/100 km</span>
              </div>
              {parameter.tankvorgaenge.map((t) => (
                <div key={t.id} className="rechenwerte__zeile">
                  <span>{datum(t.datum)}</span>
                  <span className={t.istHoechsterPreis ? 'hervor' : undefined}>
                    {zahl(t.preisProLiter, 2)}
                  </span>
                  <span
                    className={t.istHoechsterVerbrauch ? 'hervor' : undefined}
                  >
                    {t.verbrauchL100km == null
                      ? '–'
                      : zahl(t.verbrauchL100km, 2)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="tafel">
        <p className="etikett">Vorgaben für neue Ausflüge</p>
        <div className="feld">
          <label htmlFor="zusatz">Zusatzkosten pro Kilometer</label>
          <input
            id="zusatz"
            inputMode="decimal"
            value={zusatz}
            onChange={(e) => setZusatz(e.target.value)}
            onBlur={uebernehmen}
          />
        </div>
        <div className="feld" style={{ marginBottom: 0 }}>
          <label htmlFor="anteil">Anteil einer Einzelstrecke in Prozent</label>
          <input
            id="anteil"
            inputMode="numeric"
            value={anteil}
            onChange={(e) => setAnteil(e.target.value)}
            onBlur={anteilUebernehmen}
          />
          <p className="hinweis">
            Wer nur hin oder nur zurück mitfährt, zahlt diesen Anteil des vollen
            Beitrags. Über 50 %, weil das Auto die ganze Strecke fahren muss.
            Beide Vorgaben werden beim Anlegen am Ausflug festgehalten.
          </p>
        </div>
      </section>

      <section className="tafel">
        <div className="feld" style={{ marginBottom: 0 }}>
          <label htmlFor="initKm">Zählerstand beim Start in km</label>
          <input
            id="initKm"
            inputMode="numeric"
            value={initKm}
            onChange={(e) => setInitKm(e.target.value)}
            onBlur={initKmUebernehmen}
          />
          <p className="hinweis">
            Der abgelesene Stand, mit dem die Aufzeichnung begonnen hat. Er ist
            der Anfang der Zählerkette – eine Änderung verschiebt die
            Jahreskilometer in der Statistik. Ohne Tausenderpunkt eintragen.
          </p>
        </div>
        {/*
          Ohne diese Angabe hat die erste Strecke der Aufzeichnung nie einen
          Verbrauch: der erste Volltank findet keinen Vorgänger (R-25).
        */}
        <label className="schalter" style={{ marginTop: 14, marginBottom: 0 }}>
          <input
            type="checkbox"
            checked={settings.initTankVoll}
            onChange={(e) =>
              void aufSettings({ ...settings, initTankVoll: e.target.checked })
            }
          />
          Der Tank war dabei voll
        </label>
        <p className="hinweis">
          Dann ergibt schon der erste Volltank danach einen Verbrauch. Wer es
          nicht mehr weiss, lässt den Haken weg – dann fängt die
          Verbrauchsrechnung erst beim zweiten Volltank an.
        </p>
      </section>

      <section className="tafel">
        <p className="etikett">Daten</p>
        <div className="zeile">
          <span>Alle Vorgänge als CSV</span>
          <button
            className="knopf knopf--leise"
            onClick={() => void api.ladeCsvHerunter()}
          >
            Herunterladen
          </button>
        </div>
        <div className="zeile">
          <span>Zugang auf diesem Gerät</span>
          <button
            className="knopf knopf--leise"
            onClick={() => {
              setzeWort(null);
              location.reload();
            }}
          >
            Zugangswort neu eingeben
          </button>
        </div>

      </section>
    </>
  );
}
